import React, { useEffect, useState, useCallback } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Switch } from "../../ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui/tabs";
import { ShippingMethodsSection, DiscountCodesSection } from "./ShippingAndDiscountSections";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { toast } from "sonner";
import { Package, FolderOpen, Layers, BadgePercent, History, Plus, Save, Copy, Power, Trash2, CreditCard, ShoppingBag, RefreshCw, Mail, CheckCircle2, XCircle, AlertTriangle, Boxes } from "lucide-react";
import VariationsEditor from "./VariationsEditor";
import ImageField from "../shared/ImageField";
import GalleryField from "../shared/GalleryField";

const TYPE_LABELS = { simple: "Simple", variable: "Variable", service: "Service" };
const STOCK_LABELS = {
  in_stock: "Op voorraad", out_of_stock: "Niet op voorraad",
  backorder: "Backorder", "n/a": "n.v.t.",
};
const STOCK_COLORS = {
  in_stock: "bg-green-100 text-green-700",
  out_of_stock: "bg-red-100 text-red-700",
  backorder: "bg-orange-100 text-orange-700",
  "n/a": "bg-gray-100 text-gray-600",
};
const STRIPE_LABELS = {
  not_synced: "Niet gesync.", synced: "Gesync.", error: "Fout", "n/a": "n.v.t.",
};

function ProductsTab() {
  const { isAdminOrOwner, hasPermission, user } = useAuth();
  const isOwner = user?.role === "owner";
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vatRates, setVatRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState({ q: "", category_id: "", product_type: "", stock_status: "" });
  const [hideTestData, setHideTestData] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(false);

  const canCreate = hasPermission("webshop:create");
  const canEdit = hasPermission("webshop:edit");
  const canManagePrices = hasPermission("webshop:manage_prices");
  const canManageStock = hasPermission("webshop:manage_stock");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filter).filter(([_, v]) => v));
      const [p, c, v] = await Promise.all([
        adminApi.get("/admin/webshop/products", { params }),
        adminApi.get("/admin/webshop/categories"),
        adminApi.get("/admin/webshop/vat-rates"),
      ]);
      setProducts(p.data); setCategories(c.data); setVatRates(v.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  // F5 — Test/junk data detector. Matches the patterns the user
  // identified during the GAP-REPORT: "(kopie)" in name, SKU prefixes
  // STK-/TEST-, name starting with "Stock Test " / "TEST Product ",
  // and SKU containing -COPY-.
  const isTestData = (p) => {
    const name = (p.name || "").trim();
    const sku = (p.sku || "").trim();
    if (/\(kopie\)/i.test(name)) return true;
    if (/-COPY-/i.test(sku)) return true;
    if (/^STK-/i.test(sku)) return true;
    if (/^TEST-/i.test(sku)) return true;
    if (/^Stock Test /i.test(name)) return true;
    if (/^TEST Product /i.test(name)) return true;
    return false;
  };

  const testCount = products.filter(isTestData).length;
  const visibleProducts = hideTestData
    ? products.filter((p) => !isTestData(p))
    : products;

  const onBulkArchiveTestData = async () => {
    if (!isOwner) {
      toast.error("Alleen Owner mag bulk-archiveren");
      return;
    }
    const targets = products.filter(isTestData);
    if (targets.length === 0) {
      toast.info("Geen testdata gevonden om te archiveren");
      return;
    }
    const ok = window.confirm(
      `${targets.length} test/kopie-producten worden gedeactiveerd en uit de webshop verborgen. ` +
      `Gekoppelde Stripe-producten worden ook gearchiveerd waar mogelijk. ` +
      `Bestaande orders en voorraadgeschiedenis blijven ongewijzigd.\n\nDoorgaan?`
    );
    if (!ok) return;
    setBulkBusy(true);
    try {
      const r = await adminApi.post("/admin/webshop/products/bulk-archive", {
        product_ids: targets.map((p) => p.id),
      });
      const { archived, stripe_archived, stripe_errors } = r.data;
      toast.success(
        `${archived} gearchiveerd · Stripe: ${stripe_archived} ok` +
        (stripe_errors?.length ? `, ${stripe_errors.length} fout` : "")
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const empty = {
    name: "", sku: "", category_id: "", short_description: "", description: "",
    image_url: "", image_alt: "", image_title: "", gallery_images: [], product_type: "simple",
    regular_price: 0, sale_price: null, vat_percentage: 21.0,
    stock_qty: 0, stock_status: "in_stock",
    weight: null, length: null, width: null, height: null, cbm: null,
    active: true, featured: false, visible_on_webshop: true, sort_order: 0,
  };

  const onSave = async () => {
    if (!editing.name?.trim()) { toast.error("Naam is verplicht"); return; }
    try {
      const payload = { ...editing };
      ["regular_price", "sale_price", "stock_qty", "vat_percentage",
       "weight", "length", "width", "height", "cbm", "sort_order"].forEach((k) => {
        if (payload[k] === "" || payload[k] === undefined) payload[k] = null;
        else if (payload[k] !== null) payload[k] = Number(payload[k]);
      });
      if (editing.id) await adminApi.put(`/admin/webshop/products/${editing.id}`, payload);
      else await adminApi.post("/admin/webshop/products", payload);
      toast.success("Product opgeslagen");
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDuplicate = async (id) => {
    try { await adminApi.post(`/admin/webshop/products/${id}/duplicate`); toast.success("Gedupliceerd"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const onToggle = async (id) => {
    try { await adminApi.post(`/admin/webshop/products/${id}/activate`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const onDelete = async (id) => {
    if (!isAdminOrOwner()) return;
    if (!window.confirm("Product verwijderen?")) return;
    try { await adminApi.delete(`/admin/webshop/products/${id}`); toast.success("Verwijderd"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="webshop-products-list">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-sm text-gray-600">Beheer producten, prijzen, voorraad en zichtbaarheid op de webshop.</p>
        {canCreate && (
          <Button onClick={() => setEditing({ ...empty })} className="bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs" data-testid="webshop-product-new">
            <Plus className="w-4 h-4 mr-1" /> Nieuw product
          </Button>
        )}
      </div>

      {/* F5 — Test data hide toggle + Owner bulk archive */}
      {testCount > 0 && (
        <div
          className="bg-amber-50 border border-amber-200 p-3 mb-3 flex flex-wrap items-center justify-between gap-2 text-sm"
          data-testid="webshop-testdata-banner"
        >
          <div className="flex items-center gap-2 text-amber-900">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>
              <strong>{testCount}</strong> test/kopie-producten gedetecteerd
              <span className="text-xs text-amber-700 ml-1">
                (matches op &quot;(kopie)&quot;, SKU STK-/TEST-/-COPY-, &quot;Stock Test&quot;)
              </span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase font-bold flex items-center gap-2" data-testid="webshop-hide-testdata-label">
              <Switch
                checked={hideTestData}
                onCheckedChange={setHideTestData}
                data-testid="webshop-hide-testdata-toggle"
              />
              Verberg testdata
            </label>
            {isOwner && (
              <Button
                onClick={onBulkArchiveTestData}
                disabled={bulkBusy}
                className="rounded-none bg-red-600 hover:bg-red-700 text-white font-chivo font-bold uppercase text-xs"
                data-testid="webshop-bulk-archive-button"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                {bulkBusy ? "Bezig…" : `Archiveer ${testCount} testdata`}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 p-3 mb-4 grid grid-cols-1 sm:grid-cols-4 gap-2">
        <Input placeholder="Zoek op naam/SKU…" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} className="rounded-none" data-testid="webshop-filter-q" />
        <Select value={filter.category_id || "all"} onValueChange={(v) => setFilter({ ...filter, category_id: v === "all" ? "" : v })}>
          <SelectTrigger className="rounded-none" data-testid="webshop-filter-category"><SelectValue placeholder="Alle categorieën" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Alle categorieën</SelectItem>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filter.product_type || "all"} onValueChange={(v) => setFilter({ ...filter, product_type: v === "all" ? "" : v })}>
          <SelectTrigger className="rounded-none" data-testid="webshop-filter-type"><SelectValue placeholder="Alle types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle types</SelectItem>
            <SelectItem value="simple">Simple</SelectItem>
            <SelectItem value="variable">Variable</SelectItem>
            <SelectItem value="service">Service</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filter.stock_status || "all"} onValueChange={(v) => setFilter({ ...filter, stock_status: v === "all" ? "" : v })}>
          <SelectTrigger className="rounded-none" data-testid="webshop-filter-stock"><SelectValue placeholder="Alle voorraadstatussen" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle voorraadstatussen</SelectItem>
            <SelectItem value="in_stock">Op voorraad</SelectItem>
            <SelectItem value="out_of_stock">Niet op voorraad</SelectItem>
            <SelectItem value="backorder">Backorder</SelectItem>
            <SelectItem value="n/a">n.v.t.</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Editor */}
      {editing && (
        <div className="bg-white border border-gray-200 p-4 mb-4" data-testid="webshop-product-form">
          <h3 className="font-chivo font-bold text-base mb-3">{editing.id ? "Product bewerken" : "Nieuw product"}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div><Label className="text-xs uppercase font-bold">Naam</Label><Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-none mt-1" data-testid="webshop-form-name" /></div>
            <div><Label className="text-xs uppercase font-bold">SKU</Label><Input value={editing.sku || ""} onChange={(e) => setEditing({ ...editing, sku: e.target.value })} className="rounded-none mt-1" data-testid="webshop-form-sku" /></div>
            <div>
              <Label className="text-xs uppercase font-bold">Categorie</Label>
              <Select value={editing.category_id || ""} onValueChange={(v) => setEditing({ ...editing, category_id: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="webshop-form-category"><SelectValue placeholder="Kies…" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase font-bold">Type</Label>
              <Select value={editing.product_type} onValueChange={(v) => setEditing({ ...editing, product_type: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="webshop-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="simple">Simple product</SelectItem>
                  <SelectItem value="variable">Variable product</SelectItem>
                  <SelectItem value="service">Service product</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase font-bold">Normale prijs (€)</Label><Input type="number" step="0.01" value={editing.regular_price ?? ""} onChange={(e) => setEditing({ ...editing, regular_price: e.target.value })} className="rounded-none mt-1" disabled={!canManagePrices && !!editing.id} data-testid="webshop-form-regular-price" /></div>
            <div><Label className="text-xs uppercase font-bold">Actieprijs (€)</Label><Input type="number" step="0.01" value={editing.sale_price ?? ""} onChange={(e) => setEditing({ ...editing, sale_price: e.target.value })} className="rounded-none mt-1" disabled={!canManagePrices && !!editing.id} /></div>
            <div>
              <Label className="text-xs uppercase font-bold">BTW %</Label>
              <Select value={String(editing.vat_percentage ?? 21)} onValueChange={(v) => setEditing({ ...editing, vat_percentage: Number(v) })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="webshop-form-vat"><SelectValue /></SelectTrigger>
                <SelectContent>{vatRates.map((r) => <SelectItem key={r.id} value={String(r.percentage)}>{r.name} ({r.percentage}%)</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase font-bold">Voorraad</Label><Input type="number" value={editing.stock_qty ?? 0} onChange={(e) => setEditing({ ...editing, stock_qty: e.target.value })} className="rounded-none mt-1" disabled={!canManageStock && !!editing.id} data-testid="webshop-form-stock-qty" /></div>
            <div>
              <Label className="text-xs uppercase font-bold">Voorraadstatus</Label>
              <Select value={editing.stock_status} onValueChange={(v) => setEditing({ ...editing, stock_status: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="webshop-form-stock-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="in_stock">Op voorraad</SelectItem>
                  <SelectItem value="out_of_stock">Niet op voorraad</SelectItem>
                  <SelectItem value="backorder">Backorder</SelectItem>
                  <SelectItem value="n/a">n.v.t.</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 lg:col-span-3"><Label className="text-xs uppercase font-bold">Korte omschrijving</Label><Textarea rows={2} value={editing.short_description || ""} onChange={(e) => setEditing({ ...editing, short_description: e.target.value })} className="rounded-none mt-1" /></div>
            <div className="sm:col-span-2 lg:col-span-3"><Label className="text-xs uppercase font-bold">Lange omschrijving</Label><Textarea rows={4} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="rounded-none mt-1" /></div>
            <div className="sm:col-span-2 lg:col-span-3">
              <ImageField
                value={editing.image_url}
                onChange={(url) => setEditing({ ...editing, image_url: url })}
                label="Afbeelding (hoofdfoto)"
                testIdPrefix="webshop-form-image"
                helperText="Upload PNG/JPG/WEBP/GIF tot 5 MB, of kies bestaande afbeelding uit de Media Library."
              />
            </div>
            <div><Label className="text-xs uppercase font-bold">Alt-tekst (SEO)</Label><Input value={editing.image_alt || ""} onChange={(e) => setEditing({ ...editing, image_alt: e.target.value })} className="rounded-none mt-1" placeholder="Beschrijving van de afbeelding" data-testid="webshop-form-image-alt" /></div>
            <div><Label className="text-xs uppercase font-bold">Afbeelding titel</Label><Input value={editing.image_title || ""} onChange={(e) => setEditing({ ...editing, image_title: e.target.value })} className="rounded-none mt-1" data-testid="webshop-form-image-title" /></div>
            <div className="sm:col-span-2 lg:col-span-3 mt-3 pt-3 border-t border-gray-200">
              <GalleryField
                value={editing.gallery_images || []}
                onChange={(next) => setEditing({ ...editing, gallery_images: next })}
                testIdPrefix="webshop-form-gallery"
              />
            </div>
            <div><Label className="text-xs uppercase font-bold">Gewicht (kg)</Label><Input type="number" step="0.01" value={editing.weight ?? ""} onChange={(e) => setEditing({ ...editing, weight: e.target.value })} className="rounded-none mt-1" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs uppercase font-bold">L (cm)</Label><Input type="number" value={editing.length ?? ""} onChange={(e) => setEditing({ ...editing, length: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label className="text-xs uppercase font-bold">B (cm)</Label><Input type="number" value={editing.width ?? ""} onChange={(e) => setEditing({ ...editing, width: e.target.value })} className="rounded-none mt-1" /></div>
              <div><Label className="text-xs uppercase font-bold">H (cm)</Label><Input type="number" value={editing.height ?? ""} onChange={(e) => setEditing({ ...editing, height: e.target.value })} className="rounded-none mt-1" /></div>
            </div>
            <div className="flex items-end gap-3"><Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="webshop-form-active" /><Label className="text-xs uppercase font-bold">Actief</Label></div>
            <div className="flex items-end gap-3"><Switch checked={!!editing.featured} onCheckedChange={(v) => setEditing({ ...editing, featured: v })} data-testid="webshop-form-featured" /><Label className="text-xs uppercase font-bold">Uitgelicht</Label></div>
            <div className="flex items-end gap-3"><Switch checked={!!editing.visible_on_webshop} onCheckedChange={(v) => setEditing({ ...editing, visible_on_webshop: v })} data-testid="webshop-form-visible" /><Label className="text-xs uppercase font-bold">Zichtbaar webshop</Label></div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <Button onClick={onSave} className="rounded-none bg-[#0055FF] hover:bg-[#0A192F] text-white font-chivo font-bold uppercase text-xs" data-testid="webshop-form-save"><Save className="w-4 h-4 mr-1" /> Opslaan</Button>
            <Button variant="outline" className="rounded-none" onClick={() => setEditing(null)}>Annuleren</Button>
          </div>

          {/* F3 — Stripe-status indicator (replaces obsolete Phase 4A placeholder banner) */}
          {editing.id && editing.product_type !== "variable" && (
            <div className="mt-4" data-testid="product-stripe-status">
              {editing.stripe_sync_status === "synced" && (
                <div className="text-xs text-green-700 bg-green-50 border border-green-200 px-3 py-2 flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span><strong>Stripe:</strong> gesynchroniseerd · price ID <code className="font-mono">{editing.stripe_price_id || "—"}</code></span>
                </div>
              )}
              {editing.stripe_sync_status === "error" && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 flex items-start gap-2">
                  <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Stripe sync-fout:</strong>{" "}
                    <span className="font-mono break-all">{editing.stripe_sync_error || "Onbekende fout"}</span>
                    <div className="mt-1 text-red-600">Gebruik de Stripe-tab → <em>Markeer alles als sync nodig</em> + <em>Sync alles</em> om dit te herstellen.</div>
                  </div>
                </div>
              )}
              {(!editing.stripe_sync_status || editing.stripe_sync_status === "n/a") && (
                <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 px-3 py-2">
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                  <strong>Stripe:</strong> nog niet gesynchroniseerd. Run de Stripe-tab → <em>Sync alles met Stripe</em> om dit product koopbaar te maken.
                </div>
              )}
              {editing.stripe_sync_status === "pending" && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2">
                  <RefreshCw className="w-3.5 h-3.5 inline mr-1" />
                  <strong>Stripe:</strong> sync nodig. Run de Stripe-tab → <em>Sync alles met Stripe</em>.
                </div>
              )}
            </div>
          )}

          {/* F1 — Variations editor: only for variable products that already exist */}
          {editing.product_type === "variable" && (
            editing.id ? (
              <VariationsEditor productId={editing.id} productName={editing.name} />
            ) : (
              <div className="border border-amber-200 bg-amber-50 p-4 mt-4 text-sm text-amber-800" data-testid="variations-needs-save">
                <Layers className="w-4 h-4 inline mr-1" />
                Sla het product eerst op — daarna kunt u hier variaties (bijv. Maat S, Maat L) toevoegen.
              </div>
            )
          )}
        </div>
      )}

      {/* List */}
      <div className="bg-white border border-gray-200 overflow-x-auto">
        {loading ? <div className="p-6 text-gray-500">Laden…</div> :
         visibleProducts.length === 0 ? <div className="p-6 text-gray-500">Geen producten gevonden.</div> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
              <th className="py-3 px-4 font-bold">Naam</th>
              <th className="py-3 px-4 font-bold">SKU</th>
              <th className="py-3 px-4 font-bold">Type</th>
              <th className="py-3 px-4 font-bold">Prijs</th>
              <th className="py-3 px-4 font-bold">BTW</th>
              <th className="py-3 px-4 font-bold">Voorraad</th>
              <th className="py-3 px-4 font-bold">Stripe</th>
              <th className="py-3 px-4 font-bold">Status</th>
              <th className="py-3 px-4 font-bold">Acties</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {visibleProducts.map((p) => (
                <tr key={p.id} data-testid={`webshop-product-row-${p.id}`}>
                  <td className="py-3 px-4 font-bold">{p.name}</td>
                  <td className="py-3 px-4 font-mono text-xs">{p.sku || "—"}</td>
                  <td className="py-3 px-4 text-xs">{TYPE_LABELS[p.product_type]}</td>
                  <td className="py-3 px-4">€ {Number(p.regular_price ?? 0).toFixed(2)}</td>
                  <td className="py-3 px-4 text-xs">{p.vat_percentage ?? 0}%</td>
                  <td className="py-3 px-4 text-xs">
                    {p.product_type === "variable" ? (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700"
                        title={`${p.variations_aggregate?.active_count ?? 0} actieve varianten`}
                        data-testid={`webshop-product-aggregate-${p.id}`}
                      >
                        <Boxes className="w-3 h-3" />
                        {(p.variations_aggregate?.count ?? 0)} var · {(p.variations_aggregate?.total_stock ?? 0)} totaal
                      </span>
                    ) : (
                      <span className={`inline-block px-2 py-0.5 ${STOCK_COLORS[p.stock_status]}`}>{p.stock_qty ?? 0} · {STOCK_LABELS[p.stock_status]}</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-xs text-gray-500" data-testid={`webshop-product-stripe-${p.id}`}>
                    {p.product_type === "variable" ? (
                      (() => {
                        const a = p.variations_aggregate || {};
                        if ((a.active_count ?? 0) === 0) return <span className="text-gray-400">–</span>;
                        if ((a.errored ?? 0) > 0) {
                          return (
                            <span className="bg-red-100 text-red-700 px-2 py-0.5" title={`${a.errored} variant(en) met fout`}>
                              {a.errored} fout
                            </span>
                          );
                        }
                        if ((a.synced ?? 0) === (a.active_count ?? 0)) {
                          return <span className="bg-green-100 text-green-700 px-2 py-0.5">Gesync. {a.synced}/{a.active_count}</span>;
                        }
                        return <span className="bg-amber-100 text-amber-700 px-2 py-0.5">{a.synced}/{a.active_count}</span>;
                      })()
                    ) : (
                      STRIPE_LABELS[p.stripe_sync_status || "n/a"]
                    )}
                  </td>
                  <td className="py-3 px-4 text-xs">{p.active ? <span className="text-green-700 font-bold">Actief</span> : <span className="text-gray-400">Inactief</span>}</td>
                  <td className="py-3 px-4 flex items-center gap-2">
                    {canEdit && <button onClick={() => setEditing({ ...p })} className="text-xs text-[#0055FF] hover:underline" data-testid={`webshop-product-edit-${p.id}`}>Bewerken</button>}
                    {canCreate && <button onClick={() => onDuplicate(p.id)} className="text-xs text-gray-600 hover:text-[#0055FF]" title="Dupliceren" data-testid={`webshop-product-duplicate-${p.id}`}><Copy className="w-3 h-3 inline" /></button>}
                    {canEdit && <button onClick={() => onToggle(p.id)} className="text-xs text-gray-600 hover:text-[#0055FF]" title={p.active ? "Deactiveren" : "Activeren"} data-testid={`webshop-product-toggle-${p.id}`}><Power className="w-3 h-3 inline" /></button>}
                    {isAdminOrOwner() && <button onClick={() => onDelete(p.id)} className="text-xs text-red-500 hover:underline" data-testid={`webshop-product-delete-${p.id}`}><Trash2 className="w-3 h-3 inline" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function CategoriesTab() {
  const { isAdminOrOwner, hasPermission } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const canManage = hasPermission("webshop:manage_categories");

  const load = useCallback(async () => {
    try { const r = await adminApi.get("/admin/webshop/categories"); setItems(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSave = async () => {
    if (!editing.name?.trim()) { toast.error("Naam verplicht"); return; }
    try {
      if (editing.id) await adminApi.put(`/admin/webshop/categories/${editing.id}`, editing);
      else await adminApi.post("/admin/webshop/categories", editing);
      toast.success("Opgeslagen"); setEditing(null); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="webshop-categories-list">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-600">Productcategorieën beheren.</p>
        {canManage && <Button onClick={() => setEditing({ name: "", slug: "", description: "", active: true, sort_order: 0 })} className="bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs" data-testid="webshop-category-new"><Plus className="w-4 h-4 mr-1" /> Nieuwe categorie</Button>}
      </div>
      {editing && (
        <div className="bg-white border border-gray-200 p-4 mb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input placeholder="Naam" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-none" data-testid="webshop-category-form-name" />
          <Input placeholder="Slug (auto)" value={editing.slug || ""} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} className="rounded-none" />
          <Input placeholder="Volgorde" type="number" value={editing.sort_order || 0} onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) })} className="rounded-none" />
          <Textarea placeholder="Omschrijving" rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="rounded-none sm:col-span-3" />
          <div className="flex items-center gap-2"><Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} /><Label className="text-xs uppercase font-bold">Actief</Label></div>
          <div className="sm:col-span-3 flex gap-2"><Button onClick={onSave} className="rounded-none bg-[#0055FF] hover:bg-[#0A192F] text-white font-chivo font-bold uppercase text-xs" data-testid="webshop-category-save"><Save className="w-4 h-4 mr-1" /> Opslaan</Button><Button variant="outline" onClick={() => setEditing(null)}>Annuleren</Button></div>
        </div>
      )}
      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase text-gray-500 border-b"><th className="py-3 px-4 font-bold">Naam</th><th className="py-3 px-4 font-bold">Slug</th><th className="py-3 px-4 font-bold">Volgorde</th><th className="py-3 px-4 font-bold">Actief</th><th className="py-3 px-4 font-bold">Acties</th></tr></thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((c) => (
              <tr key={c.id} data-testid={`webshop-category-row-${c.id}`}>
                <td className="py-3 px-4 font-bold">{c.name}</td>
                <td className="py-3 px-4 font-mono text-xs">{c.slug}</td>
                <td className="py-3 px-4">{c.sort_order ?? 0}</td>
                <td className="py-3 px-4 text-xs">{c.active ? "Ja" : "Nee"}</td>
                <td className="py-3 px-4 text-xs">{canManage && <button onClick={() => setEditing({ ...c })} className="text-[#0055FF] hover:underline" data-testid={`webshop-category-edit-${c.id}`}>Bewerken</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function VatTab() {
  const { hasPermission } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const canManage = hasPermission("webshop:manage_vat");

  const load = useCallback(async () => {
    try { const r = await adminApi.get("/admin/webshop/vat-rates"); setItems(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSave = async () => {
    try {
      const payload = { ...editing, percentage: Number(editing.percentage) };
      if (editing.id) await adminApi.put(`/admin/webshop/vat-rates/${editing.id}`, payload);
      else await adminApi.post("/admin/webshop/vat-rates", payload);
      toast.success("Opgeslagen"); setEditing(null); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="webshop-vat-list">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-600">BTW tarieven beheren — gebruikt door producten en varianten.</p>
        {canManage && <Button onClick={() => setEditing({ name: "", percentage: 21, active: true, is_default: false })} className="bg-[#050505] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs" data-testid="webshop-vat-new"><Plus className="w-4 h-4 mr-1" /> Nieuw BTW tarief</Button>}
      </div>
      {editing && (
        <div className="bg-white border border-gray-200 p-4 mb-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
          <Input placeholder="Naam (bv. 21%)" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="rounded-none" data-testid="webshop-vat-form-name" />
          <Input placeholder="Percentage" type="number" step="0.01" value={editing.percentage ?? 21} onChange={(e) => setEditing({ ...editing, percentage: e.target.value })} className="rounded-none" data-testid="webshop-vat-form-pct" />
          <div className="flex items-center gap-2"><Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} /><Label className="text-xs uppercase font-bold">Actief</Label></div>
          <div className="flex items-center gap-2"><Switch checked={!!editing.is_default} onCheckedChange={(v) => setEditing({ ...editing, is_default: v })} /><Label className="text-xs uppercase font-bold">Standaard</Label></div>
          <div className="sm:col-span-4 flex gap-2"><Button onClick={onSave} className="rounded-none bg-[#0055FF] hover:bg-[#0A192F] text-white font-chivo font-bold uppercase text-xs" data-testid="webshop-vat-save"><Save className="w-4 h-4 mr-1" /> Opslaan</Button><Button variant="outline" onClick={() => setEditing(null)}>Annuleren</Button></div>
        </div>
      )}
      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase text-gray-500 border-b"><th className="py-3 px-4 font-bold">Naam</th><th className="py-3 px-4 font-bold">%</th><th className="py-3 px-4 font-bold">Actief</th><th className="py-3 px-4 font-bold">Standaard</th><th className="py-3 px-4 font-bold">Acties</th></tr></thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((v) => (
              <tr key={v.id} data-testid={`webshop-vat-row-${v.id}`}>
                <td className="py-3 px-4 font-bold">{v.name}</td>
                <td className="py-3 px-4">{v.percentage}%</td>
                <td className="py-3 px-4 text-xs">{v.active ? "Ja" : "Nee"}</td>
                <td className="py-3 px-4 text-xs">{v.is_default ? "Ja" : "—"}</td>
                <td className="py-3 px-4">{canManage && <button onClick={() => setEditing({ ...v })} className="text-xs text-[#0055FF] hover:underline">Bewerken</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StockLogTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { adminApi.get("/admin/webshop/stock-log").then((r) => setLogs(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  return (
    <div data-testid="webshop-stock-log">
      <p className="text-sm text-gray-600 mb-3">Laatste 200 voorraadmutaties (incl. handmatige correcties, beschadigd, verkocht-placeholder).</p>
      <div className="bg-white border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase text-gray-500 border-b"><th className="py-3 px-4 font-bold">Datum/tijd</th><th className="py-3 px-4 font-bold">Product/Variant</th><th className="py-3 px-4 font-bold">Oud</th><th className="py-3 px-4 font-bold">Nieuw</th><th className="py-3 px-4 font-bold">Diff</th><th className="py-3 px-4 font-bold">Reden</th></tr></thead>
          <tbody className="divide-y divide-gray-100">
            {logs.map((l) => (
              <tr key={l.id}>
                <td className="py-3 px-4 font-mono text-xs">{l.created_at?.slice(0, 16).replace("T", " ")}</td>
                <td className="py-3 px-4 font-mono text-xs">{l.variant_id || l.product_id}</td>
                <td className="py-3 px-4">{l.old_qty}</td>
                <td className="py-3 px-4 font-bold">{l.new_qty}</td>
                <td className={`py-3 px-4 ${l.diff > 0 ? "text-green-700" : l.diff < 0 ? "text-red-600" : "text-gray-500"}`}>{l.diff > 0 ? "+" : ""}{l.diff}</td>
                <td className="py-3 px-4 text-xs">{l.reason}</td>
              </tr>
            ))}
            {logs.length === 0 && <tr><td colSpan={6} className="p-6 text-gray-500 text-center">Nog geen voorraadmutaties.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ==================== STRIPE SYNC TAB ====================

function StripeTab() {
  const { hasPermission } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [marking, setMarking] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const canSync = hasPermission("webshop:sync_stripe");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/webshop/stripe/status");
      setStatus(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSync = async () => {
    if (!canSync) return;
    setSyncing(true);
    setLastResult(null);
    try {
      const r = await adminApi.post("/admin/webshop/stripe/sync");
      setLastResult(r.data);
      if (r.data.configured === false) {
        toast.error("Stripe is nog niet geconfigureerd");
      } else if (r.data.errors > 0) {
        toast.warning(`${r.data.synced} gesynchroniseerd, ${r.data.errors} fouten`);
      } else {
        toast.success(`Stripe sync voltooid — ${r.data.synced} nieuw, ${r.data.unchanged} ongewijzigd`);
      }
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncing(false); }
  };

  const onMarkResync = async () => {
    if (!canSync) return;
    if (!window.confirm(
      "Weet je zeker dat je alle Stripe IDs wilt resetten? "
      + "Dit forceert een volledige nieuwe sync (vereist na switch tussen test en live keys). "
      + "Geen automatische sync — je moet daarna handmatig op 'Sync alles met Stripe' klikken."
    )) return;
    setMarking(true);
    try {
      const r = await adminApi.post("/admin/webshop/stripe/mark-resync-needed");
      toast.success(`${r.data.products_cleared} producten + ${r.data.variations_cleared} varianten gereset`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setMarking(false); }
  };

  if (loading) return <div className="p-6 text-gray-500">Laden…</div>;

  return (
    <div data-testid="webshop-stripe-tab">
      <p className="text-sm text-gray-600 mb-4">
        Synchroniseer producten + varianten naar Stripe Products/Prices.
        Prijzen worden aangemaakt met <code>tax_behavior=inclusive</code> (BTW inclusief).
        Bij prijswijziging wordt automatisch een nieuwe Stripe Price aangemaakt.
      </p>

      {/* Test/Live mode warning banner */}
      <div className="bg-amber-50 border border-amber-200 p-3 mb-4 text-xs text-amber-900 flex items-start gap-2" data-testid="stripe-mode-warning">
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          <strong>Let op test- en live-mode:</strong> test-mode Stripe IDs werken niet met live keys.
          Na overschakelen tussen test en live moeten alle actieve producten en varianten opnieuw worden gesynchroniseerd.
          Gebruik daarvoor de knop <em>"Markeer alles als sync nodig"</em> en daarna <em>"Sync alles met Stripe"</em>.
        </div>
      </div>

      <div className="bg-white border border-gray-200 p-4 mb-4" data-testid="stripe-status-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-chivo font-black uppercase text-sm tracking-wider">Status</h3>
          <div className="flex items-center gap-2">
            {status?.current_mode && (
              <span
                data-testid="stripe-current-mode-badge"
                className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${
                  status.current_mode === "live"
                    ? "bg-red-50 text-red-700 border-red-200"
                    : status.current_mode === "test"
                      ? "bg-blue-50 text-blue-700 border-blue-200"
                      : "bg-gray-50 text-gray-700 border-gray-200"
                }`}
              >
                Mode: {status.current_mode}
              </span>
            )}
            {status?.configured ? (
              <span className="inline-flex items-center gap-1 text-xs font-bold uppercase text-green-700">
                <CheckCircle2 className="w-4 h-4" /> Geconfigureerd
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-bold uppercase text-amber-700">
                <AlertTriangle className="w-4 h-4" /> Niet geconfigureerd
              </span>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-700 mb-3" data-testid="stripe-status-message">{status?.message}</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <div className="border border-gray-200 p-3"><div className="uppercase text-gray-500 font-bold">Actieve producten</div><div className="text-lg font-chivo font-black" data-testid="stripe-count-active">{status?.active_products ?? 0}</div></div>
          <div className="border border-gray-200 p-3"><div className="uppercase text-gray-500 font-bold">Gesync. producten</div><div className="text-lg font-chivo font-black" data-testid="stripe-count-synced-products">{status?.synced_products ?? 0}</div></div>
          <div className="border border-gray-200 p-3"><div className="uppercase text-gray-500 font-bold">Gesync. prices</div><div className="text-lg font-chivo font-black" data-testid="stripe-count-synced-prices">{status?.synced_prices ?? 0}</div></div>
          <div className="border border-gray-200 p-3"><div className="uppercase text-gray-500 font-bold">Met fout</div><div className={`text-lg font-chivo font-black ${status?.products_with_error ? "text-red-600" : ""}`} data-testid="stripe-count-errors">{status?.products_with_error ?? 0}</div></div>
        </div>

        {(status?.mismatched_mode_products > 0 || status?.legacy_unknown_mode_products > 0) && (
          <div className="mt-3 bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="stripe-mismatch-banner">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            <strong>Mode-mismatch gedetecteerd:</strong>{" "}
            {status.mismatched_mode_products} producten en {status.mismatched_mode_variations} varianten zijn
            gesynchroniseerd in een andere Stripe mode dan de huidige keys ({status.current_mode}).
            {status.legacy_unknown_mode_products > 0 && (
              <> Daarnaast zijn er {status.legacy_unknown_mode_products} producten zonder bekende mode (oude sync).</>
            )}{" "}
            Run een nieuwe sync.
          </div>
        )}

        {Array.isArray(status?.warnings) && status.warnings.length > 0 && (
          <ul className="mt-3 space-y-1 text-xs" data-testid="stripe-warnings">
            {status.warnings.map((w, i) => (
              <li key={i} className="bg-red-50 border border-red-200 p-2 text-red-800">
                <AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> {w}
              </li>
            ))}
          </ul>
        )}

        {status && status.configured && !status.webhook_secret_set && (
          <div className="mt-3 bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="stripe-webhook-warning">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            <strong>Webhook secret ontbreekt.</strong> Vul <code>STRIPE_WEBHOOK_SECRET</code> in <code>.env</code> om de
            <code> /api/stripe/webhook</code>-endpoint te activeren. Zonder webhook worden orders niet
            automatisch op <em>paid</em> gezet en wordt de voorraad niet afgeschreven.
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <Button
          onClick={onSync}
          disabled={!canSync || syncing || !status?.configured}
          className="rounded-none bg-[#0055FF] hover:bg-[#0A192F] text-white font-chivo font-bold uppercase text-xs"
          data-testid="stripe-sync-button"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Bezig…" : "Sync alles met Stripe"}
        </Button>
        <Button
          onClick={onMarkResync}
          disabled={!canSync || marking}
          variant="outline"
          className="rounded-none font-chivo font-bold uppercase text-xs border-amber-400 text-amber-800 hover:bg-amber-50"
          data-testid="stripe-mark-resync-button"
          title="Reset alle Stripe IDs zodat de volgende sync alle producten opnieuw aanmaakt. Gebruik na switch tussen test en live."
        >
          {marking ? "Bezig…" : "Markeer alles als sync nodig"}
        </Button>
      </div>
      {!canSync && <p className="text-xs text-gray-500 mt-2">Alleen Owner, Admin of Webshop Manager kan synchroniseren.</p>}

      {lastResult && (
        <div className="bg-white border border-gray-200 p-4 mt-4" data-testid="stripe-sync-result">
          <h4 className="font-chivo font-black uppercase text-xs tracking-wider mb-2">Laatste run</h4>
          <div className="text-sm text-gray-700 mb-2">
            <strong>{lastResult.synced}</strong> gesynchroniseerd,
            <strong className="ml-1">{lastResult.unchanged}</strong> ongewijzigd,
            <strong className="ml-1">{lastResult.skipped}</strong> overgeslagen,
            <strong className={`ml-1 ${lastResult.errors ? "text-red-600" : ""}`}>{lastResult.errors}</strong> fouten
            (totaal {lastResult.total})
          </div>
          {lastResult.errors > 0 && (
            <ul className="text-xs text-red-600 list-disc pl-4 max-h-40 overflow-y-auto">
              {lastResult.results.filter((r) => r.status === "error").map((r, i) => (
                <li key={i}>{r.product_name || r.product_id}: {r.error}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ==================== ORDERS TAB ====================

const ORDER_STATUS_LABELS = {
  pending: "Openstaand", paid: "Betaald", fulfilled: "Uitgevoerd",
  cancelled: "Geannuleerd", failed: "Mislukt", refunded: "Terugbetaald",
};
const ORDER_STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-green-100 text-green-700",
  fulfilled: "bg-blue-100 text-blue-700",
  cancelled: "bg-gray-100 text-gray-700",
  failed: "bg-red-100 text-red-700",
  refunded: "bg-purple-100 text-purple-700",
};

function OrdersTab() {
  const { hasPermission, user } = useAuth();
  const isOwner = user?.role === "owner";
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "", q: "", view: "active" });
  const [selected, setSelected] = useState(null);
  const [archiveDialog, setArchiveDialog] = useState(null); // order|null
  const [archiveReason, setArchiveReason] = useState("");
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [hardDeleteDialog, setHardDeleteDialog] = useState(null); // order|null
  const [hardDeleteConfirm, setHardDeleteConfirm] = useState("");
  const [hardDeleteBusy, setHardDeleteBusy] = useState(false);

  const canManage = hasPermission("webshop:orders_manage");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.q) params.q = filter.q;
      // Phase 19-C — view selector: active (default), archived, all
      if (filter.view === "active") params.archived = false;
      else if (filter.view === "archived") params.archived = true;
      // view === "all" => omit archived param entirely
      const r = await adminApi.get("/admin/webshop/orders", { params });
      setOrders(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const onStatus = async (oid, status) => {
    if (!canManage) return;
    try {
      await adminApi.put(`/admin/webshop/orders/${oid}/status`, { status });
      toast.success("Status bijgewerkt");
      if (selected?.id === oid) {
        const r = await adminApi.get(`/admin/webshop/orders/${oid}`);
        setSelected(r.data);
      }
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onResend = async (oid) => {
    if (!canManage) return;
    try {
      const r = await adminApi.post(`/admin/webshop/orders/${oid}/resend-email`);
      const c = r.data?.customer?.status, a = r.data?.admin?.status;
      toast.success(`E-mail: klant ${c || "?"}, admin ${a || "?"}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Phase 19-C — Archive flow
  const openArchive = (order) => {
    setArchiveReason("");
    setArchiveDialog(order);
  };
  const onArchiveConfirm = async () => {
    if (!archiveDialog) return;
    setArchiveBusy(true);
    try {
      await adminApi.post(`/admin/webshop/orders/${archiveDialog.id}/archive`,
                          { reason: archiveReason.trim() || null });
      toast.success("Bestelling gearchiveerd");
      setArchiveDialog(null);
      setSelected(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setArchiveBusy(false); }
  };
  const onUnarchive = async (order) => {
    try {
      await adminApi.post(`/admin/webshop/orders/${order.id}/unarchive`);
      toast.success("Bestelling teruggezet als actief");
      if (selected?.id === order.id) setSelected(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Phase 19-C — Hard delete flow (Owner only)
  const openHardDelete = (order) => {
    setHardDeleteConfirm("");
    setHardDeleteDialog(order);
  };
  const HARD_DELETE_BLOCKED = ["paid", "fulfilled", "refunded"];
  const canHardDelete = (order) =>
    isOwner && !HARD_DELETE_BLOCKED.includes(order?.status);
  const onHardDeleteConfirm = async () => {
    if (!hardDeleteDialog) return;
    setHardDeleteBusy(true);
    try {
      await adminApi.delete(
        `/admin/webshop/orders/${hardDeleteDialog.id}`,
        { data: { confirm: hardDeleteConfirm.trim() } },
      );
      toast.success("Bestelling permanent verwijderd");
      setHardDeleteDialog(null);
      setSelected(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setHardDeleteBusy(false); }
  };

  return (
    <div data-testid="webshop-orders-tab">
      <p className="text-sm text-gray-600 mb-3">Alle bestellingen uit de webshop. Status wordt automatisch bijgewerkt via Stripe webhook.</p>

      <div className="flex flex-wrap gap-3 mb-4">
        <Input placeholder="Zoek op e-mail, naam, session/order-id…" value={filter.q}
               onChange={(e) => setFilter({ ...filter, q: e.target.value })}
               className="rounded-none max-w-xs" data-testid="orders-filter-q" />
        <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v === "__all" ? "" : v })}>
          <SelectTrigger className="rounded-none w-48" data-testid="orders-filter-status"><SelectValue placeholder="Alle statussen" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">Alle statussen</SelectItem>
            {Object.entries(ORDER_STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.view} onValueChange={(v) => setFilter({ ...filter, view: v })}>
          <SelectTrigger className="rounded-none w-48" data-testid="orders-filter-view">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Actieve bestellingen</SelectItem>
            <SelectItem value="archived">Gearchiveerde bestellingen</SelectItem>
            <SelectItem value="all">Alle bestellingen</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <div className="p-6 text-gray-500">Laden…</div> : (
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-gray-500 border-b">
              <th className="py-3 px-4 font-bold">Datum</th>
              <th className="py-3 px-4 font-bold">Order</th>
              <th className="py-3 px-4 font-bold">Klant</th>
              <th className="py-3 px-4 font-bold">Totaal</th>
              <th className="py-3 px-4 font-bold">Status</th>
              <th className="py-3 px-4 font-bold">Acties</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-100">
              {orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.id}`}>
                  <td className="py-3 px-4 font-mono text-xs">{o.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="py-3 px-4 font-mono text-xs">{o.id.slice(0, 8)}</td>
                  <td className="py-3 px-4">
                    <div className="font-medium">{o.customer?.name}</div>
                    <div className="text-xs text-gray-500">{o.customer?.email}</div>
                  </td>
                  <td className="py-3 px-4 font-bold">€ {Number(o.amount_total || 0).toFixed(2)}</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-1 font-bold uppercase ${ORDER_STATUS_COLORS[o.status] || "bg-gray-100"}`} data-testid={`order-status-${o.id}`}>
                      {ORDER_STATUS_LABELS[o.status] || o.status}
                    </span>
                    {o.archived && (
                      <span
                        className="ml-1 text-[10px] px-2 py-1 font-bold uppercase bg-gray-200 text-gray-700"
                        data-testid={`order-archived-badge-${o.id}`}
                        title={o.archive_reason || "Gearchiveerd"}
                      >
                        Gearchiveerd
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <Button size="sm" variant="outline" onClick={() => setSelected(o)} className="rounded-none text-xs" data-testid={`order-view-${o.id}`}>Open</Button>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={6} className="p-6 text-gray-500 text-center">Nog geen bestellingen.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="order-detail-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-chivo font-black text-lg">Order {selected.id.slice(0, 8)}</h3>
                <p className="text-xs text-gray-500 font-mono break-all">{selected.id}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setSelected(null)} className="rounded-none text-xs" data-testid="order-detail-close">Sluiten</Button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm mb-5">
              <div><span className="text-xs uppercase text-gray-500">Status</span><div className="font-bold">{ORDER_STATUS_LABELS[selected.status] || selected.status}</div></div>
              <div><span className="text-xs uppercase text-gray-500">Totaal</span><div className="font-bold">€ {Number(selected.amount_total || 0).toFixed(2)}</div></div>
              <div><span className="text-xs uppercase text-gray-500">Voorraad afgeschreven</span><div>{selected.stock_decremented ? "Ja" : "Nee"}</div></div>
              <div><span className="text-xs uppercase text-gray-500">E-mail verstuurd</span><div>{selected.email_sent_customer ? "Klant ✓ " : ""}{selected.email_sent_admin ? "Admin ✓" : ""}</div></div>
              <div className="col-span-2">
                <span className="text-xs uppercase text-gray-500">Stripe session</span>
                <div className="font-mono text-[10px] break-all bg-gray-50 border border-gray-100 p-1.5 mt-0.5">{selected.session_id || "—"}</div>
              </div>
            </div>

            {/* Klantgegevens — Fase A: gestructureerd voor-/achternaam, whatsapp, bedrijfsnaam */}
            <div className="mb-5" data-testid="order-customer-block">
              <h4 className="font-chivo font-black uppercase text-xs mb-2 text-[#082c50]">Klantgegevens</h4>
              <div className="bg-gray-50 border border-gray-200 p-3 grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-[10px] uppercase text-gray-500">Naam</span><div>{selected.customer?.first_name ? `${selected.customer.first_name} ${selected.customer.last_name || ""}`.trim() : (selected.customer?.name || "—")}</div></div>
                <div><span className="text-[10px] uppercase text-gray-500">E-mail</span><div className="break-all" data-testid="order-customer-email">{selected.customer?.email || "—"}</div></div>
                <div><span className="text-[10px] uppercase text-gray-500">Telefoon</span><div>{selected.customer?.phone || "—"}</div></div>
                <div><span className="text-[10px] uppercase text-gray-500">WhatsApp</span><div>{selected.customer?.whatsapp || "—"}</div></div>
                {selected.customer?.company_name && (
                  <div className="col-span-2"><span className="text-[10px] uppercase text-gray-500">Bedrijfsnaam</span><div>{selected.customer.company_name}</div></div>
                )}
                {selected.customer?.notes && (
                  <div className="col-span-2"><span className="text-[10px] uppercase text-gray-500">Opmerkingen klant</span><div className="italic text-gray-700">{selected.customer.notes}</div></div>
                )}
              </div>
            </div>

            {/* Bezorgadres — Fase A: complete NAW of legacy-fallback */}
            <div className="mb-5" data-testid="order-shipping-block">
              <h4 className="font-chivo font-black uppercase text-xs mb-2 text-[#082c50]">Bezorgadres</h4>
              {selected.shipping_address ? (
                <div className="bg-gray-50 border border-gray-200 p-3 text-sm">
                  <div>
                    {selected.shipping_address.street} {selected.shipping_address.house_number}
                    {selected.shipping_address.house_number_addition ? ` ${selected.shipping_address.house_number_addition}` : ""}
                  </div>
                  <div>{selected.shipping_address.postal_code} {selected.shipping_address.city}</div>
                  <div>{selected.shipping_address.country}</div>
                  {selected.shipping_address.delivery_note && (
                    <div className="mt-2 text-xs text-gray-700"><span className="text-[10px] uppercase text-gray-500">Bezorginstructie</span><div className="italic">{selected.shipping_address.delivery_note}</div></div>
                  )}
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="order-shipping-legacy">
                  Adres niet ingevuld — legacy order (vóór Fase A checkout-upgrade).
                </div>
              )}
            </div>

            {/* Factuuradres */}
            <div className="mb-5" data-testid="order-billing-block">
              <h4 className="font-chivo font-black uppercase text-xs mb-2 text-[#082c50]">Factuuradres</h4>
              {selected.billing_address ? (
                selected.billing_address.same_as_shipping ? (
                  <div className="bg-gray-50 border border-gray-200 p-3 text-sm italic text-gray-600">
                    Factuuradres gelijk aan bezorgadres.
                    {(selected.billing_address.company_name || selected.billing_address.vat_number || selected.billing_address.chamber_of_commerce_number) && (
                      <div className="mt-2 not-italic text-gray-800 grid grid-cols-2 gap-2 text-xs">
                        {selected.billing_address.company_name && <div><span className="text-[10px] uppercase text-gray-500">Bedrijfsnaam</span><div>{selected.billing_address.company_name}</div></div>}
                        {selected.billing_address.vat_number && <div><span className="text-[10px] uppercase text-gray-500">BTW</span><div>{selected.billing_address.vat_number}</div></div>}
                        {selected.billing_address.chamber_of_commerce_number && <div><span className="text-[10px] uppercase text-gray-500">KvK</span><div>{selected.billing_address.chamber_of_commerce_number}</div></div>}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 p-3 text-sm">
                    {selected.billing_address.company_name && <div className="font-bold">{selected.billing_address.company_name}</div>}
                    <div>
                      {selected.billing_address.street} {selected.billing_address.house_number}
                      {selected.billing_address.house_number_addition ? ` ${selected.billing_address.house_number_addition}` : ""}
                    </div>
                    <div>{selected.billing_address.postal_code} {selected.billing_address.city}</div>
                    <div>{selected.billing_address.country}</div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                      {selected.billing_address.vat_number && <div><span className="text-[10px] uppercase text-gray-500">BTW</span><div>{selected.billing_address.vat_number}</div></div>}
                      {selected.billing_address.chamber_of_commerce_number && <div><span className="text-[10px] uppercase text-gray-500">KvK</span><div>{selected.billing_address.chamber_of_commerce_number}</div></div>}
                    </div>
                  </div>
                )
              ) : (
                <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="order-billing-legacy">
                  Factuuradres niet ingevuld — legacy order.
                </div>
              )}
            </div>

            <h4 className="font-chivo font-black uppercase text-xs mb-2 text-[#082c50]">Regels</h4>
            <table className="w-full text-sm border border-gray-200 mb-4">
              <thead><tr className="bg-gray-50 text-left text-xs uppercase"><th className="py-2 px-3">Product</th><th className="py-2 px-3">Aantal</th><th className="py-2 px-3 text-right">Subtotaal</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {(selected.items || []).map((i, idx) => (
                  <tr key={idx}>
                    <td className="py-2 px-3">{i.product_name}{i.variation_name ? ` — ${i.variation_name}` : ""}<div className="text-xs text-gray-500 font-mono">{i.sku}</div></td>
                    <td className="py-2 px-3">{i.quantity}</td>
                    <td className="py-2 px-3 text-right">€ {((i.line_total_cents || 0) / 100).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Fase B — totalen met shipping + korting */}
            <div className="mb-5 text-sm" data-testid="order-totals-block">
              {selected.amount_subtotal_cents !== undefined && (
                <div className="flex justify-between py-1"><span className="text-gray-600">Subtotaal</span><span>€ {((selected.amount_subtotal_cents || 0) / 100).toFixed(2)}</span></div>
              )}
              {selected.discount && selected.discount.discount_cents > 0 && (
                <div className="flex justify-between py-1 text-emerald-700" data-testid="order-discount-row">
                  <span>Korting ({selected.discount.code}{selected.discount.type === "percentage" ? ` • ${selected.discount.value}%` : ""})</span>
                  <span>− € {(selected.discount.discount_cents / 100).toFixed(2)}</span>
                </div>
              )}
              {selected.shipping_cents > 0 && (
                <div className="flex justify-between py-1" data-testid="order-shipping-row">
                  <span>Verzending{selected.shipping?.name ? ` — ${selected.shipping.name}` : ""}{selected.shipping?.free_applied ? " (gratis)" : ""}</span>
                  <span>€ {((selected.shipping_cents || 0) / 100).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between py-2 border-t border-gray-200 mt-1 font-chivo font-black text-base text-[#082c50]">
                <span>Totaal</span><span data-testid="order-grand-total">€ {Number(selected.amount_total || 0).toFixed(2)}</span>
              </div>
              {selected.shop_customer_id && (
                <div className="text-[10px] uppercase text-[#1a5193] font-bold mt-2" data-testid="order-shop-customer-badge">
                  Gekoppeld aan shopaccount
                </div>
              )}
            </div>

            {canManage && (
              <div className="flex flex-wrap gap-2">
                <Select value={selected.status} onValueChange={(v) => onStatus(selected.id, v)}>
                  <SelectTrigger className="rounded-none w-52" data-testid="order-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ORDER_STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button size="sm" onClick={() => onResend(selected.id)} className="rounded-none bg-[#0A192F] hover:bg-[#050505] text-white text-xs uppercase font-bold" data-testid="order-resend-email">
                  <Mail className="w-4 h-4 mr-1" /> E-mail opnieuw versturen
                </Button>
                {/* Phase 19-C — Archive / Unarchive */}
                {selected.archived ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onUnarchive(selected)}
                    className="rounded-none text-xs uppercase font-bold border-gray-400"
                    data-testid="order-unarchive-button"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" /> Dearchiveren
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openArchive(selected)}
                    className="rounded-none text-xs uppercase font-bold border-gray-400"
                    data-testid="order-archive-button"
                  >
                    <Boxes className="w-4 h-4 mr-1" /> Archiveren
                  </Button>
                )}
                {/* Phase 19-C — Hard delete (Owner-only + not-paid) */}
                {canHardDelete(selected) && (
                  <Button
                    size="sm"
                    onClick={() => openHardDelete(selected)}
                    className="rounded-none bg-red-600 hover:bg-red-700 text-white text-xs uppercase font-bold"
                    data-testid="order-hard-delete-button"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Permanent verwijderen
                  </Button>
                )}
              </div>
            )}
            {selected.archived && (
              <div
                className="mt-3 text-xs text-gray-600 bg-gray-50 border border-gray-200 px-3 py-2"
                data-testid="order-archived-info"
              >
                <strong>Gearchiveerd</strong>
                {selected.archived_at ? ` op ${String(selected.archived_at).slice(0, 16).replace("T", " ")}` : ""}
                {selected.archive_reason ? ` — reden: ${selected.archive_reason}` : ""}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Phase 19-C — Archive dialog */}
      {archiveDialog && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !archiveBusy && setArchiveDialog(null)}
          data-testid="order-archive-dialog"
        >
          <div className="bg-white max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-chivo font-black text-lg mb-1">Bestelling archiveren</h3>
            <p className="text-xs text-gray-500 mb-3">
              Order <strong className="font-mono">{archiveDialog.id.slice(0, 8)}</strong> ·{" "}
              € {Number(archiveDialog.amount_total || 0).toFixed(2)} ·{" "}
              {archiveDialog.customer?.email}
            </p>
            <p className="text-xs text-gray-600 mb-3">
              De bestelling verdwijnt uit het standaard overzicht maar blijft volledig in de
              database (financiële data, klant, Stripe session). Stripe-betaling wordt niet
              gewijzigd of teruggeboekt. Vindbaar via filter <em>Gearchiveerde bestellingen</em>.
            </p>
            <Label className="text-xs uppercase font-bold">Reden (optioneel)</Label>
            <Input
              value={archiveReason}
              onChange={(e) => setArchiveReason(e.target.value)}
              placeholder="Bijv. dubbele test-order"
              className="rounded-none mt-1"
              data-testid="order-archive-reason"
            />
            <div className="mt-4 flex gap-2 justify-end">
              <Button variant="outline" className="rounded-none"
                      onClick={() => setArchiveDialog(null)} disabled={archiveBusy}>
                Annuleren
              </Button>
              <Button
                onClick={onArchiveConfirm}
                disabled={archiveBusy}
                className="rounded-none bg-[#0055FF] hover:bg-[#0A192F] text-white font-chivo font-bold uppercase text-xs"
                data-testid="order-archive-confirm"
              >
                {archiveBusy ? "Bezig…" : "Archiveren"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 19-C — Hard delete dialog (Owner only) */}
      {hardDeleteDialog && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !hardDeleteBusy && setHardDeleteDialog(null)}
          data-testid="order-hard-delete-dialog"
        >
          <div className="bg-white max-w-md w-full p-6 border-t-4 border-red-600"
               onClick={(e) => e.stopPropagation()}>
            <h3 className="font-chivo font-black text-lg mb-1 text-red-700">
              Bestelling permanent verwijderen
            </h3>
            <p className="text-xs text-gray-700 mb-2">
              Order <strong className="font-mono">{hardDeleteDialog.id.slice(0, 8)}</strong> ·{" "}
              € {Number(hardDeleteDialog.amount_total || 0).toFixed(2)} ·{" "}
              status <strong>{ORDER_STATUS_LABELS[hardDeleteDialog.status] || hardDeleteDialog.status}</strong>
            </p>
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 mb-3">
              <strong>Deze actie kan niet ongedaan worden gemaakt.</strong>
              <ul className="list-disc ml-4 mt-1 space-y-0.5">
                <li>Order verdwijnt definitief uit de database.</li>
                <li>Stripe-session blijft staan; geen refund.</li>
                <li>Audit-log behoudt het bewijs.</li>
                <li>Voor betaalde orders: gebruik <em>Archiveren</em>.</li>
              </ul>
            </div>
            <Label className="text-xs uppercase font-bold">
              Typ <span className="font-mono text-red-700">{hardDeleteDialog.id.slice(0, 8)}</span> om te bevestigen
            </Label>
            <Input
              value={hardDeleteConfirm}
              onChange={(e) => setHardDeleteConfirm(e.target.value)}
              placeholder={hardDeleteDialog.id.slice(0, 8)}
              className="rounded-none mt-1 font-mono"
              data-testid="order-hard-delete-confirm"
              autoFocus
            />
            <div className="mt-4 flex gap-2 justify-end">
              <Button variant="outline" className="rounded-none"
                      onClick={() => setHardDeleteDialog(null)} disabled={hardDeleteBusy}>
                Annuleren
              </Button>
              <Button
                onClick={onHardDeleteConfirm}
                disabled={hardDeleteBusy || hardDeleteConfirm.toLowerCase() !== hardDeleteDialog.id.slice(0, 8).toLowerCase()}
                className="rounded-none bg-red-600 hover:bg-red-700 text-white font-chivo font-bold uppercase text-xs"
                data-testid="order-hard-delete-final"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                {hardDeleteBusy ? "Verwijderen…" : "Definitief verwijderen"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WebshopSection() {
  const [tab, setTab] = useState("products");
  return (
    <div data-testid="admin-section-webshop">
      <h1 className="font-chivo font-black text-3xl text-[#0A192F] tracking-tight">Webshopbeheer</h1>
      <p className="text-sm text-gray-600 mt-1">Beheer producten, categorieën, varianten, voorraad, BTW, Stripe-sync en bestellingen.</p>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="rounded-none bg-white border border-gray-200 p-0 h-auto flex-wrap" data-testid="webshop-tabs">
          <TabsTrigger value="products" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="webshop-tab-products"><Package className="w-4 h-4 mr-1 inline" /> Producten</TabsTrigger>
          <TabsTrigger value="categories" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="webshop-tab-categories"><FolderOpen className="w-4 h-4 mr-1 inline" /> Categorieën</TabsTrigger>
          <TabsTrigger value="vat" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="webshop-tab-vat"><BadgePercent className="w-4 h-4 mr-1 inline" /> BTW</TabsTrigger>
          <TabsTrigger value="stock" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="webshop-tab-stock"><History className="w-4 h-4 mr-1 inline" /> Voorraadmutaties</TabsTrigger>
          <TabsTrigger value="orders" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="webshop-tab-orders"><ShoppingBag className="w-4 h-4 mr-1 inline" /> Bestellingen</TabsTrigger>
          <TabsTrigger value="shipping" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="webshop-tab-shipping">Verzending</TabsTrigger>
          <TabsTrigger value="discounts" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="webshop-tab-discounts">Kortingen</TabsTrigger>
          <TabsTrigger value="stripe" className="rounded-none data-[state=active]:bg-[#0A192F] data-[state=active]:text-white" data-testid="webshop-tab-stripe"><CreditCard className="w-4 h-4 mr-1 inline" /> Stripe</TabsTrigger>
        </TabsList>
        <TabsContent value="products" className="mt-4"><ProductsTab /></TabsContent>
        <TabsContent value="categories" className="mt-4"><CategoriesTab /></TabsContent>
        <TabsContent value="vat" className="mt-4"><VatTab /></TabsContent>
        <TabsContent value="stock" className="mt-4"><StockLogTab /></TabsContent>
        <TabsContent value="orders" className="mt-4"><OrdersTab /></TabsContent>
        <TabsContent value="shipping" className="mt-4"><ShippingMethodsSection /></TabsContent>
        <TabsContent value="discounts" className="mt-4"><DiscountCodesSection /></TabsContent>
        <TabsContent value="stripe" className="mt-4"><StripeTab /></TabsContent>
      </Tabs>
    </div>
  );
}
